import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

const NewsTab = ({ providerId }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (providerId) {
      fetchNews();
    }
  }, [providerId]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        ?.from('provider_news')
        ?.select('*')
        ?.eq('provider_id', providerId)
        ?.eq('status', 'published')
        ?.order('published_at', { ascending: false });

      if (fetchError) throw fetchError;
      setArticles(data || []);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Nieuws laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
        <Icon name="AlertCircle" size={32} className="text-error mx-auto mb-2" />
        <p className="text-error">{error}</p>
      </div>
    );
  }

  if (articles?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon name="Newspaper" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-bold text-foreground mb-2">Geen nieuws beschikbaar</h3>
        <p className="text-muted-foreground">Deze aanbieder heeft nog geen nieuwsartikelen gepubliceerd.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Featured Article (First one) */}
      {articles?.[0] && (
        <div className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-elevation-2 transition-shadow">
          {articles?.[0]?.image_url && (
            <div className="aspect-video bg-muted overflow-hidden">
              <img
                src={articles?.[0]?.image_url}
                alt={articles?.[0]?.title || 'Nieuws afbeelding'}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-3">
              <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                Uitgelicht
              </span>
              <span className="text-sm text-muted-foreground">
                {format(new Date(articles[0]?.published_at), 'd MMMM yyyy', { locale: nl })}
              </span>
            </div>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-3">
              {articles?.[0]?.title}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {articles?.[0]?.excerpt || articles?.[0]?.content?.substring(0, 200) + '...'}
            </p>
            <div className="flex items-center space-x-2 text-primary">
              <span className="text-sm font-medium">Lees meer</span>
              <Icon name="ArrowRight" size={16} />
            </div>
          </div>
        </div>
      )}
      {/* Other Articles Grid */}
      {articles?.length > 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles?.slice(1)?.map((article, idx) => (
            <motion.div
              key={article?.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-elevation-2 transition-shadow"
            >
              {article?.image_url ? (
                <div className="aspect-video bg-muted overflow-hidden">
                  <img
                    src={article?.image_url}
                    alt={article?.title || 'Nieuws afbeelding'}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-muted flex items-center justify-center">
                  <Icon name="Newspaper" size={48} className="text-muted-foreground" />
                </div>
              )}
              <div className="p-4">
                <p className="text-xs text-muted-foreground mb-2">
                  {format(new Date(article?.published_at), 'd MMMM yyyy', { locale: nl })}
                </p>
                <h3 className="text-lg font-heading font-bold text-foreground mb-2 line-clamp-2">
                  {article?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {article?.excerpt || article?.content?.substring(0, 150) + '...'}
                </p>
                <div className="flex items-center space-x-2 text-primary">
                  <span className="text-sm font-medium">Lees meer</span>
                  <Icon name="ArrowRight" size={14} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default NewsTab;